import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Bookmark, Save, Printer, Eye, RefreshCcw, Trash2, Loader2 } from 'lucide-react';
import { thumbUrl } from './TemplateGallery';

export const designToState = (cfg) => ({
  perPage: cfg.per_page, showBusinessName: cfg.show_business_name, showSpaceName: cfg.show_space_name,
  showUnitName: cfg.show_unit_name, showInstructions: cfg.show_instructions, instructionsText: cfg.instructions_text,
  wifiText: cfg.wifi_text, socialText: cfg.social_text, qrColor: cfg.qr_color, cutMarks: cfg.cut_marks,
  logoSource: cfg.logo_source
});

const SaveDialog = ({ open, onOpenChange, selectedCount, saving, onSave }) => {
  const [name, setName] = useState('');
  const [includeUnits, setIncludeUnits] = useState(true);
  const submit = async () => { if (await onSave(name.trim(), includeUnits && selectedCount > 0)) setName(''); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="qr-save-design-dialog">
        <DialogHeader>
          <DialogTitle>Guardar diseño</DialogTitle>
          <DialogDescription>Guarda plantilla, textos, logo, color, marcas de corte y cantidad por hoja.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="design-name">Nombre</Label>
            <Input id="design-name" data-testid="qr-design-name-input" value={name} maxLength={60}
              onChange={(e) => setName(e.target.value)} placeholder="Ej: Terraza verano" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox data-testid="qr-design-include-units" checked={includeUnits && selectedCount > 0}
              disabled={selectedCount === 0} onCheckedChange={(v) => setIncludeUnits(!!v)} />
            Incluir las mesas seleccionadas ({selectedCount})
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="qr-design-save-cancel">Cancelar</Button>
          <Button onClick={submit} disabled={!name.trim() || saving} data-testid="qr-design-save-confirm">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DesignItem = ({ design, templateName, busy, onLoad, onReprint, onUpdate, onDelete }) => {
  const n = design.config.unit_ids.length;
  return (
    <div data-testid={`qr-design-${design.design_id}`} className="shrink-0 w-80 flex gap-3 p-2 rounded-lg border bg-card hover:border-primary/40 transition-colors">
      <img src={thumbUrl(design.config.template_id)} alt="" className="w-14 h-[70px] rounded object-cover bg-muted" />
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="font-medium text-sm truncate" data-testid={`qr-design-name-${design.design_id}`}>{design.name}</div>
        <div className="text-[11px] text-muted-foreground leading-snug line-clamp-2">
          {templateName} · {design.config.per_page}/hoja · {design.include_units ? `${n} mesas` : 'sin mesas'}
        </div>
        <div className="flex gap-1 mt-auto pt-1">
          <Button size="sm" className="h-7 px-2 text-xs" disabled={busy} onClick={() => onReprint(design)} data-testid={`qr-design-reprint-${design.design_id}`}>
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Printer className="w-3 h-3 mr-1" />}Reimprimir
          </Button>
          <Button size="icon" variant="outline" className="h-7 w-7" title="Cargar y revisar" onClick={() => onLoad(design)} data-testid={`qr-design-load-${design.design_id}`}><Eye className="w-3 h-3" /></Button>
          <Button size="icon" variant="outline" className="h-7 w-7" title="Actualizar con lo actual" onClick={() => onUpdate(design)} data-testid={`qr-design-update-${design.design_id}`}><RefreshCcw className="w-3 h-3" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" title="Eliminar" onClick={() => onDelete(design)} data-testid={`qr-design-delete-${design.design_id}`}><Trash2 className="w-3 h-3" /></Button>
        </div>
      </div>
    </div>
  );
};

export const SavedDesignsPanel = ({ designs, templates, selectedCount, saving, reprintingId, onSave, onLoad, onReprint, onUpdate, onDelete }) => {
  const [saveOpen, setSaveOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const tplName = (id) => templates.find(t => t.template_id === id)?.name || 'Plantilla';
  return (
    <Card className="mb-4" data-testid="qr-saved-designs">
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2"><Bookmark className="w-4 h-4" />Diseños guardados</CardTitle>
        <Button size="sm" variant="outline" onClick={() => setSaveOpen(true)} data-testid="qr-save-design-btn">
          <Save className="w-4 h-4 mr-2" />Guardar diseño actual
        </Button>
      </CardHeader>
      <CardContent>
        {designs.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="qr-designs-empty">Todavía no guardaste diseños. Armá una hoja y guardala para reimprimirla igual con un clic.</p>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-1">
            {designs.map(d => (
              <DesignItem key={d.design_id} design={d} templateName={tplName(d.config.template_id)} busy={reprintingId === d.design_id}
                onLoad={onLoad} onReprint={onReprint} onUpdate={onUpdate} onDelete={setToDelete} />
            ))}
          </div>
        )}
      </CardContent>
      <SaveDialog open={saveOpen} onOpenChange={setSaveOpen} selectedCount={selectedCount} saving={saving}
        onSave={async (name, inc) => { const ok = await onSave(name, inc); if (ok) setSaveOpen(false); return ok; }} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar “{toDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>Esto no afecta los QRs de las mesas.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="qr-design-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="qr-design-delete-confirm" onClick={() => { onDelete(toDelete); setToDelete(null); }}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
